import { type ElasticsearchQuery, type FacetFieldConfig } from '@searchkit/api';

import { FILTERS_CONFIGS } from '~/search-domain/config/filters-config';
import {
  allWNBAQuery,
  eplOnlySoccerQuery,
  lineReductionQuery,
  noSoccerQuery,
  noSpreadBetsQuery,
  noWorldCup26Query,
  onlyFootballQuery,
  sportsDataHistoryRequiredQuery,
  supportedCategoryGroupsQuery,
} from '~/search-domain/queries';
import {
  type SearchDomainContext,
  type SearchRuntimeSortingConfig,
  type SearchRuntimeSortingOption,
  type SearchUiContext,
} from '~/search-domain/types';
import { REPUBS } from '~/vendor/cx-modules/constants';

export const resultAttributes = [
  'aiProbability',
  'aiProjection',
  'betDescription',
  'betId',
  'betName',
  'betNameMap',
  'betTypeId',
  'bookmakerCode',
  'categoryGroupId',
  'categoryGroupName',
  'categoryId',
  'categoryName',
  'eventId',
  'eventDisplayName',
  'eventDisplayNameMap',
  'expectedValue',
  'expectedValueStatus',
  'line',
  'marketClassification',
  'marketDisplayName',
  'marketDisplayNameMap',
  'marketId',
  'marketName',
  'marketPropType',
  'oddsAmerican',
  'oddsDecimal',
  'oddsFractional',
  'parentBetId',
  'sportsData',
  'sportsbookProbability',
  'subeventId',
  'subeventName',
  'subeventStartDate',
  'subeventStartTime',
  'teamData',
  'type',
];

export function getTrendsBaseFilters(context: SearchDomainContext) {
  const baseFilters: ElasticsearchQuery[] = [noSpreadBetsQuery, sportsDataHistoryRequiredQuery];

  if (context.searchFlags.soccerEplOnly) {
    baseFilters.push(eplOnlySoccerQuery);
  }

  if (context.repubConfig.name === REPUBS.WS.name) {
    baseFilters.push(onlyFootballQuery);
  }

  if (!context.searchFlags.soccerTrends) {
    baseFilters.push(noSoccerQuery);
  }

  if (context.searchFlags.allWnba) {
    baseFilters.push(allWNBAQuery);
  }

  if (context.searchFlags.lineReduction) {
    baseFilters.push(lineReductionQuery);
  }

  if (!context.searchFlags.newCategoryGroups) {
    baseFilters.push(supportedCategoryGroupsQuery);
  }

  if (!context.searchFlags.worldCup26) {
    baseFilters.push(noWorldCup26Query);
  }

  return baseFilters;
}

export function getTrendsFacetAttributes(context: SearchDomainContext): FacetFieldConfig[] {
  const repubCountry = context.repubCountry;
  if (!repubCountry) return [];
  const areInternalSearchToolsEnabled = context.searchFlags.internalSearchTools;

  const internalSearchToolFacets: FacetFieldConfig[] = areInternalSearchToolsEnabled
    ? [{ attribute: 'bookmakerCode', field: 'bookmakerCode', type: 'string' }]
    : [];

  const facets = [
    ...internalSearchToolFacets,
    { attribute: 'aiProbability', field: 'aiProbability', type: 'numeric' },
    { attribute: 'betId', field: 'betId', type: 'numeric' },
    { attribute: 'betTypeId', field: 'betTypeId', type: 'numeric' },
    { attribute: 'categoryId', field: 'categoryId', type: 'numeric' },
    { attribute: 'categoryName', field: 'categoryName.keyword', type: 'string' },
    { attribute: 'eventDisplayName', field: 'eventDisplayName', type: 'string' },
    { attribute: 'eventId', field: 'eventId', type: 'numeric' },
    { attribute: 'expectedValue', field: 'expectedValue', type: 'numeric' },
    { attribute: 'marketId', field: 'marketId', type: 'numeric' },
    { attribute: 'marketPropType', field: 'marketPropType', type: 'string' },
    {
      attribute: `marketDisplayNameMap.${repubCountry}`,
      field: `marketDisplayNameMap.${repubCountry}.keyword`,
      type: 'string',
    },
    { attribute: 'marketDisplayNameMap.uk', field: 'marketDisplayNameMap.uk.keyword', type: 'string' },
    { attribute: 'marketDisplayNameMap.us', field: 'marketDisplayNameMap.us.keyword', type: 'string' },
    { attribute: 'oddsDecimal', field: 'oddsDecimal', type: 'numeric' },
    { attribute: 'sportsData.participant.name', field: 'sportsData.participant.name.keyword', type: 'string' },
    {
      attribute: 'sportsData.participant.participantId',
      field: 'sportsData.participant.participantId',
      type: 'string',
    },
    { attribute: 'sportsData.participant.nickName', field: 'sportsData.participant.nickName.keyword', type: 'string' },
    {
      attribute: 'sportsData.participant.shortName',
      field: 'sportsData.participant.shortName.keyword',
      type: 'string',
    },
    { attribute: 'sportsData.participant.type', field: 'sportsData.participant.type', type: 'string' },
    { attribute: 'aiProjection', field: 'aiProjection', type: 'numeric' },
    { attribute: 'subeventId', field: 'subeventId', type: 'numeric' },
    { attribute: 'subeventStartDate', field: 'subeventStartDate', type: 'string' },
    { attribute: 'subeventStartDateLocal', field: 'subeventStartDateLocal', type: 'string' },
    { attribute: 'subeventStartTime', field: 'subeventStartTime', type: 'string' },
    { attribute: 'sportsData.historyCount', field: 'sportsData.historyCount', type: 'numeric' },
    { attribute: 'sportsData.hitRates.last10', field: 'sportsData.hitRates.last10', type: 'numeric' },
    { attribute: 'teamData.away.name', field: 'teamData.away.name.keyword', type: 'string' },
    { attribute: 'teamData.away.nickName', field: 'teamData.away.nickName.keyword', type: 'string' },
    { attribute: 'teamData.away.shortName', field: 'teamData.away.shortName.keyword', type: 'string' },
    { attribute: 'teamData.away.nameMap.uk', field: 'teamData.away.nameMap.uk.keyword', type: 'string' },
    { attribute: 'teamData.away.nameMap.us', field: 'teamData.away.nameMap.us.keyword', type: 'string' },
    { attribute: 'teamData.away.nameMap.it', field: 'teamData.away.nameMap.it.keyword', type: 'string' },
    { attribute: 'teamData.home.name', field: 'teamData.home.name.keyword', type: 'string' },
    { attribute: 'teamData.home.nickName', field: 'teamData.home.nickName.keyword', type: 'string' },
    { attribute: 'teamData.home.shortName', field: 'teamData.home.shortName.keyword', type: 'string' },
    { attribute: 'teamData.home.nameMap.uk', field: 'teamData.home.nameMap.uk.keyword', type: 'string' },
    { attribute: 'teamData.home.nameMap.us', field: 'teamData.home.nameMap.us.keyword', type: 'string' },
    { attribute: 'teamData.home.nameMap.it', field: 'teamData.home.nameMap.it.keyword', type: 'string' },
    { attribute: 'type', field: 'type', type: 'string' },
  ] satisfies FacetFieldConfig[];

  return facets;
}

export function getTrendsSearchSettings(context: SearchUiContext) {
  const repubCountry = context.repubCountry;
  if (!repubCountry) {
    return {
      highlights: [],
      search: [],
    };
  }

  const isMatchCentreTab = context.usageContext === 'match-centre-tab';

  const teamDataAttributes = [
    'teamData.away.name',
    'teamData.away.nickName',
    'teamData.away.shortName',
    'teamData.away.nameMap.uk',
    'teamData.away.nameMap.us',
    'teamData.away.nameMap.it',
    'teamData.home.name',
    'teamData.home.nickName',
    'teamData.home.shortName',
    'teamData.home.nameMap.uk',
    'teamData.home.nameMap.us',
    'teamData.home.nameMap.it',
  ];

  const searchAttributes = [
    `marketDisplayNameMap.${repubCountry}`,
    'sportsData.participant.name',
    'sportsData.participant.nickName',
    'sportsData.participant.shortName',
  ];

  if (!isMatchCentreTab) {
    searchAttributes.push(...teamDataAttributes);
  }

  return {
    highlights: searchAttributes,
    search: searchAttributes,
    results: resultAttributes,
  };
}

export function getTrendsSorting(context: SearchUiContext): SearchRuntimeSortingConfig {
  const repubName = context.repubConfig.name;
  const translations = FILTERS_CONFIGS[repubName as keyof typeof FILTERS_CONFIGS] ?? FILTERS_CONFIGS.OC;
  const isMatchCentreTab = context.usageContext === 'match-centre-tab';

  const subeventStartTime: SearchRuntimeSortingOption = {
    field: 'subeventStartTime',
    label: translations.sortEventTime,
    order: 'asc',
  };

  const options: SearchRuntimeSortingOption[] = [
    ...(!isMatchCentreTab ? [subeventStartTime] : []),
    { field: 'sportsData.hitRates.last10', label: translations.sortHitRate10HL, order: 'desc' },
    { field: 'sportsData.hitRates.last10', label: translations.sortHitRate10LH, order: 'asc' },
    { field: 'sportsData.hitRates.last5', label: translations.sortHitRate5HL, order: 'desc' },
    { field: 'sportsData.hitRates.last5', label: translations.sortHitRate5LH, order: 'asc' },
    { field: 'sportsData.hitRates.last20', label: translations.sortHitRate20HL, order: 'desc' },
    { field: 'sportsData.hitRates.last20', label: translations.sortHitRate20LH, order: 'asc' },
    { field: 'oddsDecimal', label: translations.sortBestOddsHL, order: 'desc' },
    { field: 'oddsDecimal', label: translations.sortBestOddsLH, order: 'asc' },
  ];

  return { options };
}
